import { forwardRef, Suspense, useRef } from "react"
import { useParams } from "react-router-dom"
import { enqueueSnackbar } from "notistack"
import { useNavigate } from "react-router"
import { FormikProps } from "formik"
import { MERCHANT_ADMIN_ROUTE, MERCHANT_ADMIN_SHOPS_LIST_ROUTE } from "../../../../../constants/routes"
import { QueryErrorBoundary } from "../../../../../core/api/components/QueryErrorBoundary"
import { StandardButton } from "../../../../../shared/components/buttons/StandardButton"
import { LoadingSpinner } from "../../../../../shared/components/loading/LoadingSpinner"
import { shopDetails, ShopDetailsResponse, ShopsQuery, updateShop } from "../shopsApi"
import { PageHeader } from "../../../../../shared/components/pageHeader/PageHeader"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { Page } from "../../../../../shared/components/pageHeader/PageHeaderApi"
import { queryKeyFor, useApiQuery } from "../../../../../core/api/useApiQuery"
import { CreateUpdateShopForm } from "../components/CreateUpdateShopForm"
import { useApiMutation } from "../../../../../core/api/useApiMutation"
import { prepareInitialValues } from "../helpers/prepareInitialValues"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../shared/components/links/StandardBreadcrumbs"
import { CreateOrEditShopFormValues } from "../shopsTypes"

export function EditShop() {
    const formRef = useRef<FormikProps<CreateOrEditShopFormValues>>(null)

    const handleSaveClick = () => {
        formRef.current?.submitForm()
    }

    return (
        <>
            <LocalizedStrict id="merchant-shop-edit-header" attrs={{ title: true }}>
                <PageHeader
                    page={Page.MERCHANT_ADMIN_SHOPS_EDIT}
                    title="Edit shop"
                    rightElement={
                        <LocalizedStrict id="merchant-shop-save-button">
                            <StandardButton onClick={handleSaveClick}>Save</StandardButton>
                        </LocalizedStrict>
                    }
                >
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="merchant-nav-item-home">
                            <BreadcrumbLink to={MERCHANT_ADMIN_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="merchant-nav-item-shops">
                            <Breadcrumb>Shops</Breadcrumb>
                        </LocalizedStrict>
                        <LocalizedStrict id="merchant-nav-item-shops">
                            <BreadcrumbLink to={MERCHANT_ADMIN_SHOPS_LIST_ROUTE}>Shops</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="merchant-shop-edit-breadcrumb">
                            <Breadcrumb>Edit shop</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>

            <QueryErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                    <ShopContent ref={formRef} />
                </Suspense>
            </QueryErrorBoundary>
        </>
    )
}

function transformData({ shop }: ShopDetailsResponse) {
    return shop
}

const ShopContent = forwardRef<FormikProps<CreateOrEditShopFormValues>>((props, ref) => {
    const navigate = useNavigate()
    const { shopId } = useParams()

    const { data: shopDetailsResult } = useApiQuery(shopDetails, {
        queryName: ShopsQuery.DETAILS,
        dependencies: {
            shopId: shopId || "",
        },
        enabled: Boolean(shopId),
        suspense: true,
        useErrorBoundary: true,
        select: transformData,
    })

    const { mutate: callUpdateShop } = useApiMutation(updateShop, {
        async onSuccess(params, data, context) {
            await context.queryClient.invalidateQueries({ queryKey: [ShopsQuery.LIST] })
            await context.queryClient.invalidateQueries({
                queryKey: queryKeyFor(ShopsQuery.DETAILS, {
                    shopId: params.shopId,
                }),
            })
            navigate(MERCHANT_ADMIN_SHOPS_LIST_ROUTE)
            if (!data.serviceResponse.displayMessage) return
            enqueueSnackbar({
                variant: "success",
                message: data?.serviceResponse.displayMessage,
            })
        },
    })

    return (
        <CreateUpdateShopForm
            ref={ref}
            initialValues={prepareInitialValues(shopDetailsResult)}
            onSubmit={(data) => {
                shopId && callUpdateShop({ shop: data, shopId })
            }}
        />
    )
})
